// src/pages/servicos/ordensServico/OrdemServicoPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaFileInvoice } from 'react-icons/fa';
import { FiAlertCircle, FiArrowRight, FiCheckCircle, FiClipboard, FiClock, FiCreditCard, FiEdit2, FiFileText, FiList, FiPackage, FiPlus, FiPrinter, FiRotateCcw, FiSave, FiSearch, FiTool, FiTrash2, FiUser, FiXCircle } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../../../components/input/Input';
import Select from '../../../components/input/Select';
import PageContent from '../../../components/geral/PageContent';
import PageFooter from '../../../components/geral/PageFooter';
import { useConfigModulo } from '../../../contexts/administrativo/ConfigModuloContext';
import { useFilial } from '../../../contexts/administrativo/FilialContext';
import { useMessage } from '../../../contexts/geral/MessageContext';
import { usePrinterOS } from '../../../contexts/geral/PrinterOSContext';
import { useYesNo } from '../../../contexts/geral/YesNoContext';
import { useUser } from '../../../contexts/geral/UserContext';
import { useCliente } from '../../../contexts/pessoas/ClienteContext';
import { useConfigWhats } from '../../../contexts/pedidos/ConfigWhatsContext';
import { useProduto } from '../../../contexts/produtos/ProdutoContext';
import { useEquipamentoCliente } from '../../../contexts/servicos/EquipamentoClienteContext';
import { useOrdemServico } from '../../../contexts/servicos/OrdemServicoContext';
import { useTecnicoResponsavel } from '../../../contexts/servicos/TecnicoResponsavelContext';
import { useNota } from '../../../contexts/vendas/NotaContext';
import {
  dataHora,
  FATURAMENTOS_OS,
  moeda,
  PAGAMENTOS_OS,
  OrdemServico,
  PRIORIDADES_OS,
  SITUACOES_OS,
} from '../../../models/servicos/OrdemServico';
import { Nota } from '../../../models/vendas/Nota';
import { NotaItem } from '../../../models/vendas/NotaItem';
import ModalFinalizarVenda from '../../vendas/ModalFinalizarVenda';
import OrdemServicoAcaoModal from './OrdemServicoAcaoModal';
import OrdemServicoItemModal from './OrdemServicoItemModal';
import './Servicos.css';
import './OrdemServicoPage.css';

const acoes = {
  1: [
    ['iniciar-analise', 'Iniciar análise'],
    ['concluir', 'Concluir'],
  ],
  2: [
    ['enviar-aprovacao', 'Enviar para aprovação'],
    ['aguardar-cliente', 'Aguardar cliente'],
    ['concluir', 'Concluir'],
  ],
  3: [
    ['aprovar', 'Aprovar'],
    ['reprovar', 'Reprovar'],
  ],
  4: [['iniciar-execucao', 'Iniciar execução']],
  5: [
    ['aguardar-peca', 'Aguardar peça'],
    ['aguardar-cliente', 'Aguardar cliente'],
    ['concluir', 'Concluir'],
  ],
  6: [['iniciar-execucao', 'Retomar execução']],
  7: [['iniciar-execucao', 'Retomar execução']],
  // O backend valida novamente os vínculos fiscais; o botão pode ser exibido mesmo quando
  // uma marcação antiga de faturamento precisa ser normalizada durante a reabertura.
  8: [
    ['entregar', 'Registrar entrega'],
    ['reabrir', 'Reabrir OS'],
  ],
  9: [['reabrir', 'Reabrir OS']],
};

const dadosEditaveisOrdem = ordemServico => ({
  clienteId: ordemServico.clienteId || 0,
  equipamentoClienteId: ordemServico.equipamentoClienteId || null,
  tecnicoResponsavelId: ordemServico.tecnicoResponsavelId || null,
  prioridade: ordemServico.prioridade,
  problemaRelatado: ordemServico.problemaRelatado || '',
  observacaoEntrada: ordemServico.observacaoEntrada || '',
  diagnostico: ordemServico.diagnostico || '',
  solucaoAplicada: ordemServico.solucaoAplicada || '',
  observacaoInterna: ordemServico.observacaoInterna || '',
  observacaoCliente: ordemServico.observacaoCliente || '',
  previsaoConclusao: ordemServico.previsaoConclusao || null,
  valorDesconto: Number(ordemServico.valorDesconto || 0),
  garantiaDias: ordemServico.garantiaDias || null,
  itens: (ordemServico.itens || []).map(item => ({
    id: item.id || 0,
    produtoId: item.produtoId,
    xProd: item.xProd,
    quantidade: Number(item.quantidade),
    valorUnitario: Number(item.valorUnitario),
    valorDesconto: Number(item.valorDesconto || 0),
    autorizadoCliente: Boolean(item.autorizadoCliente),
    executado: Boolean(item.executado),
    observacao: item.observacao || '',
  })),
});

const serializarOrdem = ordemServico => JSON.stringify(dadosEditaveisOrdem(ordemServico));

const iconesAcoes = {
  concluir: FiCheckCircle,
  aprovar: FiCheckCircle,
  reprovar: FiXCircle,
  entregar: FiPackage,
  reabrir: FiRotateCcw,
  'aguardar-cliente': FiClock,
  'aguardar-peca': FiClock,
};

const OrdemServicoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { filialLogin } = useFilial();
  const { configModuloSel } = useConfigModulo();
  const usaPrioridadeOS = configModuloSel?.usaPrioridadeOS === true;
  const { user } = useUser();
  const { showMessage } = useMessage();
  const { imprimirOrdemServicoPDF } = usePrinterOS();
  const { showYesNo } = useYesNo();
  const { configWhats } = useConfigWhats();
  const { clientes, showClienteSearch, showClienteCad } = useCliente();
  const { produtos } = useProduto();
  const { buscaNotaByOrdemServico } = useNota();
  const api = useOrdemServico();
  const {
    equipamentos,
    listarEquipamentos,
    handleShowEquipamentoClienteSearch,
    showEquipamentoClienteCad,
  } = useEquipamentoCliente();
  const {
    tecnicos,
    listar: listarTecnicos,
    handleShowTecnicoResponsavelSearch,
    showTecnicoResponsavelCad,
  } = useTecnicoResponsavel();

  const [ordem, setOrdem] = useState(new OrdemServico());
  const [showItem, setShowItem] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  const [indiceItemEdicao, setIndiceItemEdicao] = useState(null);
  const [acao, setAcao] = useState(null);
  const [showFinalizarVenda, setShowFinalizarVenda] = useState(false);
  const [nota, setNota] = useState(new Nota());
  const [faturando, setFaturando] = useState(false);
  const [versaoSalva, setVersaoSalva] = useState('');
  const ordemBuscaAutomaticaRef = useRef(null);
  const confirmandoSaidaRef = useRef(false);
  const ignorarAvisoSaidaRef = useRef(false);
  const versaoAtual = useMemo(() => serializarOrdem(ordem), [ordem]);
  const temAlteracoes = Boolean(versaoSalva) && versaoAtual !== versaoSalva;

  const aplicarOrdemSalva = ordemSalva => {
    setOrdem(ordemSalva);
    setVersaoSalva(serializarOrdem(ordemSalva));
  };

  const confirmarSaida = async () => {
    if (!temAlteracoes || ignorarAvisoSaidaRef.current) return true;
    if (confirmandoSaidaRef.current) return false;

    confirmandoSaidaRef.current = true;
    try {
      return await showYesNo(
        'Existem alterações não salvas. Deseja sair desta tela e descartá-las?',
        'no',
      );
    } finally {
      confirmandoSaidaRef.current = false;
    }
  };

  const navegarComConfirmacao = async destino => {
    if (await confirmarSaida()) {
      navigate(destino);
    }
  };

  useEffect(() => {
    const carregar = async () => {
      if (id) {
        if (ordemBuscaAutomaticaRef.current === id) return;

        ordemBuscaAutomaticaRef.current = id;
        const ordemEncontrada = await api.obterDetalhada(id);
        if (ordemEncontrada) {
          aplicarOrdemSalva(ordemEncontrada);
        }
      } else {
        ordemBuscaAutomaticaRef.current = null;
        const novaOrdem = new OrdemServico(filialLogin?.id, user?.id);
        aplicarOrdemSalva(novaOrdem);
      }
    };

    carregar();
  }, [id, filialLogin?.id, user?.id]);

  useEffect(() => {
    if (ordem.clienteId) {
      listarEquipamentos(ordem.clienteId, true);
    }
  }, [ordem.clienteId]);

  useEffect(() => {
    listarTecnicos();
  }, [listarTecnicos]);

  useEffect(() => {
    const avisarAntesDeFechar = evento => {
      if (!temAlteracoes || ignorarAvisoSaidaRef.current) return;

      evento.preventDefault();
      evento.returnValue = '';
    };

    const interceptarLink = evento => {
      if (
        !temAlteracoes
        || ignorarAvisoSaidaRef.current
        || evento.defaultPrevented
        || evento.button !== 0
        || evento.ctrlKey
        || evento.metaKey
        || evento.shiftKey
        || evento.altKey
      ) return;

      const link = evento.target.closest?.('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download')) return;

      const destino = new URL(link.href, window.location.href);
      const enderecoAtual = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const enderecoDestino = `${destino.pathname}${destino.search}${destino.hash}`;
      if (destino.origin === window.location.origin && enderecoDestino === enderecoAtual) return;

      evento.preventDefault();
      evento.stopPropagation();

      confirmarSaida().then(deveSair => {
        if (!deveSair) return;

        if (destino.origin === window.location.origin) {
          navigate(enderecoDestino);
        } else {
          ignorarAvisoSaidaRef.current = true;
          window.location.assign(destino.href);
        }
      });
    };

    window.addEventListener('beforeunload', avisarAntesDeFechar);
    document.addEventListener('click', interceptarLink, true);

    return () => {
      window.removeEventListener('beforeunload', avisarAntesDeFechar);
      document.removeEventListener('click', interceptarLink, true);
    };
  }, [temAlteracoes, navigate, showYesNo]);

  const alterar = (campo, valor) => {
    setOrdem(anterior => ({
      ...anterior,
      [campo]: valor,
    }));
  };

  const abrirNovoItem = () => {
    setItemEdicao(null);
    setIndiceItemEdicao(null);
    setShowItem(true);
  };

  const abrirEdicaoItem = (item, indice) => {
    setItemEdicao(item);
    setIndiceItemEdicao(indice);
    setShowItem(true);
  };

  const fecharItem = () => {
    setShowItem(false);
    setItemEdicao(null);
    setIndiceItemEdicao(null);
  };

  const salvarItem = async item => {
    const itensAtualizados = indiceItemEdicao === null
      ? [...ordem.itens, item]
      : ordem.itens.map((itemAtual, indice) => (
        indice === indiceItemEdicao ? item : itemAtual
      ));

    const ordemComItens = {
      ...ordem,
      itens: itensAtualizados,
    };
    const ordemAtualizada = await api.atualizar(
      ordem.id,
      payloadAtualizacao(ordemComItens),
    );

    if (ordemAtualizada) {
      aplicarOrdemSalva(ordemAtualizada);
      fecharItem();
    }
  };

  const removerItem = async indice => {
    const ordemComItens = {
      ...ordem,
      itens: ordem.itens.filter((_, posicao) => posicao !== indice),
    };
    const ordemAtualizada = await api.atualizar(
      ordem.id,
      payloadAtualizacao(ordemComItens),
    );

    if (ordemAtualizada) {
      aplicarOrdemSalva(ordemAtualizada);
    }
  };

  const total = useMemo(
    () => ordem.itens.reduce(
      (soma, item) => (
        soma
        + Number(item.quantidade) * Number(item.valorUnitario)
        - Number(item.valorDesconto || 0)
      ),
      0,
    ) - Number(ordem.valorDesconto || 0),
    [ordem.itens, ordem.valorDesconto],
  );

  const selecionarCliente = clienteSelecionado => {
    if (!clienteSelecionado?.id) return;

    setOrdem(anterior => ({
      ...anterior,
      clienteId: clienteSelecionado.id,
      clienteNome: clienteSelecionado.pessoa?.nome || clienteSelecionado.nome || '',
      equipamentoClienteId: null,
    }));
  };

  const handleClienteExit = (clienteId, clienteFallback = null) => {
    if (!clienteId) {
      setOrdem(anterior => ({
        ...anterior,
        clienteId: 0,
        clienteNome: '',
        equipamentoClienteId: null,
      }));
      return;
    }

    const clienteSelecionado = clienteFallback || clientes.find(
      item => String(item.id) === String(clienteId),
    );

    if (clienteSelecionado) {
      selecionarCliente(clienteSelecionado);
    } else {
      showMessage('Cliente não encontrado!', 'warning');
      setOrdem(anterior => ({
        ...anterior,
        clienteId: 0,
        clienteNome: '',
        equipamentoClienteId: null,
      }));
    }
  };

  const handleBuscarCliente = async () => {
    const clienteSelecionado = await showClienteSearch();
    if (clienteSelecionado?.id) {
      handleClienteExit(clienteSelecionado.id, clienteSelecionado);
    }
  };

  const handleCadastrarCliente = async () => {
    const clienteSelecionado = clientes.find(
      item => String(item.id) === String(ordem.clienteId),
    );
    const clienteSalvo = await showClienteCad(clienteSelecionado, false);

    if (clienteSalvo?.id) {
      handleClienteExit(clienteSalvo.id, clienteSalvo);
    }
  };

  const selecionarEquipamento = equipamento => {
    if (!equipamento?.id) return;
    alterar('equipamentoClienteId', equipamento.id);
  };

  const exigirClienteParaEquipamento = () => {
    if (ordem.clienteId) return true;

    showMessage(
      'Informe o cliente antes de buscar ou cadastrar um equipamento!',
      'warning',
    );
    return false;
  };

  const handleEquipamentoExit = equipamentoId => {
    if (!equipamentoId) {
      alterar('equipamentoClienteId', null);
      return;
    }

    if (!exigirClienteParaEquipamento()) {
      alterar('equipamentoClienteId', null);
      return;
    }

    const selecionado = equipamentos.find(item => (
      Number(item.id) === Number(equipamentoId)
      && Number(item.clienteId) === Number(ordem.clienteId)
    ));

    if (selecionado) {
      selecionarEquipamento(selecionado);
    } else {
      showMessage('Equipamento não encontrado para o cliente selecionado!', 'warning');
      alterar('equipamentoClienteId', null);
    }
  };

  const selecionarTecnico = tecnico => {
    setOrdem(anterior => ({
      ...anterior,
      tecnicoResponsavelId: tecnico?.id || null,
      tecnicoResponsavelNome: tecnico?.nome || '',
    }));
  };

  const handleTecnicoExit = tecnicoId => {
    if (!tecnicoId) {
      selecionarTecnico(null);
      return;
    }
    const tecnico = tecnicos.find(item => Number(item.id) === Number(tecnicoId));
    if (tecnico) {
      selecionarTecnico(tecnico);
    } else {
      showMessage('Técnico responsável não encontrado!', 'warning');
      selecionarTecnico(null);
    }
  };

  const handleBuscarTecnico = async () => {
    const tecnico = await handleShowTecnicoResponsavelSearch();
    if (tecnico?.id) selecionarTecnico(tecnico);
  };

  const handleCadastrarTecnico = async () => {
    const tecnicoAtual = tecnicos.find(
      item => Number(item.id) === Number(ordem.tecnicoResponsavelId),
    );
    const tecnico = await showTecnicoResponsavelCad(tecnicoAtual);
    if (tecnico?.excluido) {
      selecionarTecnico(null);
      return;
    }
    if (tecnico?.id) selecionarTecnico(tecnico);
  };

  const handleBuscarEquipamento = async () => {
    if (!exigirClienteParaEquipamento()) return;

    const selecionado = await handleShowEquipamentoClienteSearch(ordem.clienteId);
    if (selecionado?.id) {
      selecionarEquipamento(selecionado);
    }
  };

  const handleCadastrarEquipamento = async () => {
    if (!exigirClienteParaEquipamento()) return;

    const clienteSelecionado = clientes.find(
      item => Number(item.id) === Number(ordem.clienteId),
    ) || {
      id: ordem.clienteId,
      pessoa: { nome: ordem.clienteNome },
    };
    const equipamentoSelecionado = equipamentos.find(
      item => Number(item.id) === Number(ordem.equipamentoClienteId),
    );
    const equipamentoSalvo = await showEquipamentoClienteCad(
      equipamentoSelecionado || null,
      clienteSelecionado,
      true,
    );

    if (equipamentoSalvo?.id) {
      selecionarEquipamento(equipamentoSalvo);
    }
  };

  const payloadAtualizacao = (ordemBase = ordem) => ({
    ...ordemBase,
    usuarioId: user?.id,
    itens: ordemBase.itens.map(({
      id: itemId,
      produtoId,
      xProd,
      quantidade,
      valorUnitario,
      valorDesconto,
      autorizadoCliente,
      executado,
      observacao,
    }) => ({
      id: itemId || 0,
      produtoId,
      xProd,
      quantidade,
      valorUnitario,
      valorDesconto,
      autorizadoCliente,
      executado,
      observacao,
    })),
  });

  const montarNotaOrdemServico = ordemBase => {
    const notaOrdemServico = new Nota();
    const notaItens = ordemBase.itens.map((item, indice) => {
      const quantidade = Number(item.quantidade || 0);
      const valorTotal = Number(
        item.valorTotal
        ?? quantidade * Number(item.valorUnitario || 0) - Number(item.valorDesconto || 0),
      );
      const produto = produtos.find(produtoAtual => (
        Number(produtoAtual.id) === Number(item.produtoId)
      )) || {
        id: item.produtoId,
        descricao: item.xProd,
        ehServico: item.tipo === 2,
      };

      return Object.assign(new NotaItem(), {
        produtoId: item.produtoId,
        ordemServicoItemId: item.id,
        item: indice + 1,
        xProd: item.xProd,
        qtdCom: quantidade,
        vUnCom: quantidade > 0 ? valorTotal / quantidade : 0,
        vProd: valorTotal,
        produto,
      });
    });
    const valorProdutosEServicos = notaItens.reduce(
      (soma, item) => soma + Number(item.vProd || 0),
      0,
    );

    return Object.assign(notaOrdemServico, {
      filialId: ordemBase.filialId,
      clienteId: ordemBase.clienteId,
      naturezaId: 1,
      caixaId: 1,
      tipoMov: 2,
      ordensServicoIds: [ordemBase.id],
      vProd: valorProdutosEServicos,
      vDesc: Number(ordemBase.valorDesconto || 0),
      vNota: Number(ordemBase.valorTotal || 0),
      observacao: `Faturamento da OS nº ${String(ordemBase.numero).padStart(6, '0')}.`,
      notaItens,
    });
  };

  const abrirFaturamento = async (ordemBase = ordem) => {
    if (!ordemBase?.id || faturando) return;

    setFaturando(true);
    try {
      const notaExistente = await buscaNotaByOrdemServico(ordemBase.id);
      if (notaExistente === undefined) return;

      if ([2, 9].includes(Number(notaExistente?.situacaoNF))) {
        const ordemFaturada = await api.acao(ordemBase.id, 'faturar', {
          usuarioId: user?.id,
        });
        if (ordemFaturada) {
          aplicarOrdemSalva(ordemFaturada);
        }
        return;
      }

      setNota(notaExistente || montarNotaOrdemServico(ordemBase));
      setShowFinalizarVenda(true);
    } finally {
      setFaturando(false);
    }
  };

  const cancelarFaturamento = () => {
    setNota(new Nota());
    setShowFinalizarVenda(false);
  };

  const confirmarFaturamento = async (_nota, options = {}) => {
    if (!options.notaEmitida) return;

    const ordemFaturada = await api.acao(ordem.id, 'faturar', {
      usuarioId: user?.id,
    });
    if (!ordemFaturada) return;

    aplicarOrdemSalva(ordemFaturada);
    cancelarFaturamento();

    if (!configWhats?.desativarWhats
      && options.documentoFiscalEmitido
      && ordemFaturada.clientePossuiTelefone) {
      const deveEnviar = await showYesNo(
        'Deseja enviar os documentos fiscais para o cliente pelo WhatsApp?',
        'no',
      );
      if (deveEnviar) {
        await api.enviarDocumentosWhatsApp([ordemFaturada.id]);
      }
    }
  };

  const salvar = async () => {
    if (!ordem.clienteId) {
      showMessage('Informe o cliente da ordem de serviço!', 'warning');
      return;
    }

    if (!ordem.problemaRelatado?.trim()) {
      showMessage('Informe o problema relatado pelo cliente!', 'warning');
      return;
    }

    try {
      if (!ordem.id) {
        const criada = await api.criar({
          ...ordem,
          filialId: filialLogin?.id,
          usuarioId: user?.id,
          itens: undefined,
        });

        if (!criada) return;

        aplicarOrdemSalva(criada);
        navigate(`/ordemServico/${criada.id}`, { replace: true });

        const deveImprimir = await showYesNo(
          `Ordem de serviço nº ${String(criada.numero).padStart(6, '0')} aberta com sucesso. Deseja imprimir a OS?`,
          'no',
        );
        if (deveImprimir) {
          await imprimirOrdemServicoPDF(criada.id, 75);
        }
      } else {
        const ordemAtualizada = await api.atualizar(ordem.id, payloadAtualizacao());
        if (ordemAtualizada) {
          aplicarOrdemSalva(ordemAtualizada);
        }
      }
    } catch (_) {}
  };

  const confirmar = async dados => {
    try {
      if (![8, 9, 10, 11].includes(ordem.situacao)) {
        const ordemAtualizada = await api.atualizar(ordem.id, payloadAtualizacao());
        if (!ordemAtualizada) return;
        aplicarOrdemSalva(ordemAtualizada);
      }

      const resultado = acao === 'aprovar'
        ? await api.aprovar(ordem.id, {
          ...dados,
          usuarioId: user?.id,
        })
        : await api.acao(ordem.id, acao, {
          usuarioId: user?.id,
          motivo: dados.motivo,
        });

      if (!resultado) return;

      aplicarOrdemSalva(resultado);
      setAcao(null);

      if (acao === 'concluir' && (resultado.itens || []).length > 0) {
        const deveFaturar = await showYesNo(
          'A ordem de serviço foi concluída. Deseja faturar e emitir a nota agora?',
          'no',
        );
        if (deveFaturar) {
          await abrirFaturamento(resultado);
        }
      }
    } catch (_) {}
  };

  const equipamentoSelecionado = equipamentos.find(
    item => Number(item.id) === Number(ordem.equipamentoClienteId),
  );
  const equipamentoDescricao = equipamentoSelecionado
    ? [
      equipamentoSelecionado.descricao,
      equipamentoSelecionado.marcaDescricao,
      equipamentoSelecionado.modelo,
    ].filter(Boolean).join(' — ')
    : ordem.equipamentoDescricao || '';
  const tecnicoSelecionado = tecnicos.find(
    item => Number(item.id) === Number(ordem.tecnicoResponsavelId),
  );
  const tecnicoResponsavelNome = tecnicoSelecionado?.nome
    || ordem.tecnicoResponsavelNome
    || '';
  const editavel = !ordem.id || ![8, 9, 10, 11].includes(ordem.situacao);

  return (
    <div className="servicos-page ordem-servico-page">
      <header className="os-cabecalho">
        <div className="os-titulo">
          <span className="os-titulo-icone"><FiTool aria-hidden="true" /></span>
          <div>
            <h1>
              {ordem.id
                ? `OS nº ${String(ordem.numero).padStart(6, '0')}`
                : 'Nova ordem de serviço'}
            </h1>
            {Boolean(ordem.id) && (
              <div className="os-situacoes" aria-label="Situações da ordem de serviço">
                <span className={`os-badge os-situacao-${ordem.situacao}`}>
                  <FiClipboard aria-hidden="true" />
                  {SITUACOES_OS[ordem.situacao]}
                </span>
                <span className="os-badge">
                  <FiFileText aria-hidden="true" />
                  {FATURAMENTOS_OS[ordem.situacaoFaturamento] || 'Faturamento não informado'}
                </span>
                <span className={`os-badge ${ordem.possuiParcelaVencida ? 'text-danger' : ''}`}>
                  <FiCreditCard aria-hidden="true" />
                  {PAGAMENTOS_OS[ordem.situacaoPagamento] || 'Pagamento não informado'}
                  {ordem.possuiParcelaVencida && ' (vencido)'}
                </span>
              </div>
            )}
            {!ordem.id && <p className="os-subtitulo">Registre o cliente, o equipamento e o problema relatado.</p>}
            </div>
        </div>
        <div className="os-cabecalho-acoes">
          {Boolean(ordem.id) && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => imprimirOrdemServicoPDF(ordem.id, 75)}
            >
              <FiPrinter aria-hidden="true" /> Imprimir OS
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => navegarComConfirmacao('/ordemServico/consultar')}
          >
            <FiSearch aria-hidden="true" /> Consultar OS
          </button>
        </div>
      </header>

      <PageContent>
      <section className="servicos-card os-atendimento-card" aria-labelledby="os-atendimento-titulo">
        <div className="os-secao-cabecalho">
          <h2 id="os-atendimento-titulo"><FiUser aria-hidden="true" /> Atendimento</h2>
          <span className="os-secao-ajuda">Cliente e problema relatado são obrigatórios.</span>
        </div>

        <div className="os-atendimento-grid">
          <div className="os-atendimento-cliente">
            <div className="d-flex gap-2 os-selecao-relacionada">
              <Input
                label="Cliente"
                value={ordem.clienteId || ''}
                disabled={!editavel}
                size={72}
                onChange={evento => setOrdem(anterior => ({
                  ...anterior,
                  clienteId: Number(evento.target.value) || 0,
                  clienteNome: '',
                  equipamentoClienteId: null,
                }))}
                onBlur={evento => handleClienteExit(evento.target.value)}
                btnBuscar={editavel ? handleBuscarCliente : null}
                btnAdd={editavel ? handleCadastrarCliente : null}
                btnTabIndex={0}
              />
              <Input
                label="Descrição do cliente"
                value={ordem.clienteNome || ''}
                title={ordem.clienteNome || ''}
                disabled
                size="100%"
              />
            </div>
          </div>

          <div className="os-atendimento-equipamento">
            <div className="d-flex gap-2 os-selecao-relacionada">
              <Input
                label="Equipamento"
                value={ordem.equipamentoClienteId || ''}
                disabled={!editavel || !ordem.clienteId}
                btnBuscarDisabled={!editavel || !ordem.clienteId}
                btnAddDisabled={!editavel || !ordem.clienteId}
                size={72}
                onChange={evento => alterar(
                  'equipamentoClienteId',
                  Number(evento.target.value) || null,
                )}
                onBlur={evento => handleEquipamentoExit(evento.target.value)}
                btnBuscar={editavel ? handleBuscarEquipamento : null}
                btnAdd={editavel ? handleCadastrarEquipamento : null}
                btnTabIndex={0}
              />
              <Input
                label="Descrição do equipamento"
                value={equipamentoDescricao}
                title={equipamentoDescricao}
                disabled
                size="100%"
              />
            </div>
          </div>

          <div className="os-atendimento-tecnico">
            <div className="d-flex gap-2 os-selecao-relacionada">
              <Input
                label="Técnico responsável"
                value={ordem.tecnicoResponsavelId || ''}
                disabled={!editavel}
                size={72}
                onChange={evento => alterar(
                  'tecnicoResponsavelId',
                  Number(evento.target.value) || null,
                )}
                onBlur={evento => handleTecnicoExit(evento.target.value)}
                btnBuscar={editavel ? handleBuscarTecnico : null}
                btnAdd={editavel ? handleCadastrarTecnico : null}
                btnTabIndex={0}
              />
              <Input
                label="Nome do técnico"
                value={tecnicoResponsavelNome}
                title={tecnicoResponsavelNome}
                disabled
                size="100%"
              />
            </div>
          </div>

        </div>

        <div className="os-relato-grid">
          <div className="os-problema-relatado">
            <Input
              label="Problema relatado"
              value={ordem.problemaRelatado || ''}
              rows={3}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar('problemaRelatado', evento.target.value)}
            />
          </div>
          <div className="os-observacao-entrada">
            <Input
              label="Observações da entrada"
              value={ordem.observacaoEntrada || ''}
              rows={3}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar('observacaoEntrada', evento.target.value)}
            />
          </div>
        </div>

        <div className={`os-dados-entrada-grid${usaPrioridadeOS ? '' : ' os-sem-prioridade'}`}>
          {usaPrioridadeOS && (
            <div className="os-dados-prioridade">
              <Select
                label="Prioridade"
                size="100%"
                disabled={!editavel}
                value={ordem.prioridade}
                onChange={evento => alterar('prioridade', Number(evento.target.value))}
                options={Object.entries(PRIORIDADES_OS).map(([value, label]) => ({ value, label }))}
              />
            </div>
          )}
          <div className="os-previsao-conclusao">
            <Input
              label="Previsão de conclusão"
              type="datetime-local"
              value={ordem.previsaoConclusao?.slice?.(0, 16) || ''}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar(
                'previsaoConclusao',
                evento.target.value || null,
              )}
            />
          </div>
          <div className="os-garantia">
            <Input
              label="Garantia (dias)"
              type="number"
              value={ordem.garantiaDias || ''}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar(
                'garantiaDias',
                evento.target.value ? Number(evento.target.value) : null,
              )}
            />
          </div>
        </div>
        {!ordem.id && (
          <p className="os-abertura-ajuda"><FiAlertCircle aria-hidden="true" /> Após abrir a OS, você poderá adicionar produtos e serviços.</p>
        )}
      </section>

      {Boolean(ordem.id) && (
        <div className="os-operacao-grid">
          <section className="servicos-card os-produtos-servicos-card" aria-labelledby="os-itens-titulo">
            <div className="os-secao-cabecalho">
              <div>
                <h2 id="os-itens-titulo"><FiPackage aria-hidden="true" /> Produtos e serviços</h2>
                <span className="os-secao-ajuda">{ordem.itens.length} {ordem.itens.length === 1 ? 'item' : 'itens'} na ordem</span>
              </div>
              {editavel && (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={abrirNovoItem}
                >
                  <FiPlus aria-hidden="true" /> Adicionar item
                </button>
              )}
            </div>

            <div className="table-responsive os-tabela-itens-scroll">
              <table className="table table-sm align-middle os-tabela-itens">
                <caption className="visually-hidden">Produtos e serviços da ordem</caption>
                <thead>
                  <tr>
                    <th scope="col">Id</th>
                    <th scope="col">Descrição</th>
                    <th scope="col" className="os-numero">Qtd.</th>
                    <th scope="col" className="os-numero">Unitário</th>
                    <th scope="col" className="os-numero">Total</th>
                    <th scope="col">Status</th>
                    <th scope="col" className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {ordem.itens.map((item, indice) => (
                    <tr className="os-linha-item" key={`${item.id || 0}-${indice}`}>
                      <td data-label="Código" className="os-item-codigo">{item.produtoId}</td>
                      <td data-label="Descrição" className="os-item-descricao">{item.xProd}</td>
                      <td data-label="Qtd." className="os-numero">{item.quantidade}</td>
                      <td data-label="Unitário" className="os-numero">{moeda(item.valorUnitario)}</td>
                      <td data-label="Total" className="os-numero os-item-total">
                        {moeda(
                          Number(item.quantidade) * Number(item.valorUnitario)
                          - Number(item.valorDesconto || 0),
                        )}
                      </td>
                      <td data-label="Status">
                        <span className={`os-item-status ${item.executado ? 'executado' : item.autorizadoCliente ? 'autorizado' : 'pendente'}`}>
                        {item.executado
                          ? 'Executado'
                          : item.autorizadoCliente
                            ? 'Autorizado'
                            : 'Pendente'}
                        </span>
                      </td>
                      <td className="os-item-acoes">
                        {editavel && (
                          <div className="d-flex justify-content-center flex-wrap gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              aria-label={`Editar ${item.xProd}`}
                              title="Editar item"
                              onClick={() => abrirEdicaoItem(item, indice)}
                            >
                              <FiEdit2 aria-hidden="true" /><span className="visually-hidden">Editar</span>
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              aria-label={`Remover ${item.xProd}`}
                              title="Remover item"
                              disabled={api.loading}
                              onClick={() => removerItem(indice)}
                            >
                              <FiTrash2 aria-hidden="true" /><span className="visually-hidden">Remover</span>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {ordem.itens.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="os-itens-vazio">
                          <FiPackage aria-hidden="true" />
                          <strong>Nenhum item adicionado</strong>
                          {editavel && <span>Use “Adicionar item” para incluir produtos ou serviços.</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="os-total-itens">
              Total da OS <strong>{moeda(total)}</strong>
            </div>
          </section>

          <section className="servicos-card os-diagnostico-card" aria-labelledby="os-diagnostico-titulo">
            <div className="os-secao-cabecalho">
              <h2 id="os-diagnostico-titulo"><FiTool aria-hidden="true" /> Diagnóstico e execução</h2>
            </div>
            <Input
              label="Diagnóstico"
              value={ordem.diagnostico || ''}
              rows={4}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar('diagnostico', evento.target.value)}
            />
            <Input
              label="Solução aplicada"
              value={ordem.solucaoAplicada || ''}
              rows={4}
              size="100%"
              disabled={!editavel}
              onChange={evento => alterar('solucaoAplicada', evento.target.value)}
            />
          </section>
        </div>
      )}

      {ordem.historicos?.length > 0 && (
        <section className="servicos-card os-historico-card" aria-labelledby="os-historico-titulo">
          <div className="os-secao-cabecalho">
            <h2 id="os-historico-titulo"><FiList aria-hidden="true" /> Histórico</h2>
          </div>
          <div className="os-historico-scroll">
            {ordem.historicos.map(historico => (
              <div className="os-historico" key={historico.id}>
                <span className="os-historico-data"><FiClock aria-hidden="true" /> {dataHora(historico.dataHora)}</span>
                <strong>{SITUACOES_OS[historico.situacaoNova]}</strong>
                {historico.usuarioNome && ` por ${historico.usuarioNome}`}
                {historico.descricao && <p>{historico.descricao}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      </PageContent>

      <PageFooter>
        <div className="os-rodape-info">
          {Boolean(ordem.id) && (
            <div className="os-rodape-total"><span>Total da OS</span><strong>{moeda(total)}</strong></div>
          )}
          <span className={`os-estado-edicao${temAlteracoes ? ' alterado' : ''}`} role="status">
            {api.loading ? 'Carregando...' : !editavel ? 'Somente consulta' : temAlteracoes ? 'Alterações não salvas' : ordem.id ? 'Dados salvos' : 'Preencha os dados para abrir a OS'}
          </span>
        </div>
        <div className="os-rodape-acoes" aria-label="Ações da ordem de serviço">
          {Boolean(ordem.id) && (acoes[ordem.situacao] || []).map(([codigo, descricao]) => {
            const Icone = iconesAcoes[codigo] || FiArrowRight;
            return (
              <button
                type="button"
                key={codigo}
                className={`btn ${['aprovar', 'concluir', 'entregar'].includes(codigo) ? 'btn-outline-success' : codigo === 'reprovar' ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                onClick={() => setAcao(codigo)}
              >
                <Icone aria-hidden="true" /> {descricao}
              </button>
            );
          })}

          {Boolean(ordem.id)
            && [8, 9].includes(ordem.situacao)
            && Number(ordem.situacaoFaturamento) !== 3 && (
            <button
              type="button"
              className="btn btn-outline-success"
              disabled={api.loading || faturando}
              onClick={() => abrirFaturamento()}
            >
              <FaFileInvoice aria-hidden="true" /> {faturando ? 'Preparando nota...' : 'Emitir nota'}
            </button>
          )}

          {Boolean(ordem.id) && ![8, 9, 10, 11].includes(ordem.situacao) && (
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={() => setAcao('cancelar')}
            >
              <FiXCircle aria-hidden="true" /> Cancelar OS
            </button>
          )}
          <button
            type="button"
            className="btn btn-success os-salvar"
            disabled={api.loading || !editavel || !temAlteracoes}
            onClick={salvar}
          >
            <FiSave aria-hidden="true" /> {api.loading && editavel ? 'Aguarde...' : ordem.id ? 'Salvar alterações' : 'Abrir OS'}
          </button>
        </div>
      </PageFooter>

      <OrdemServicoItemModal
        show={showItem}
        item={itemEdicao}
        onHide={fecharItem}
        onSave={salvarItem}
      />
      <OrdemServicoAcaoModal
        show={Boolean(acao)}
        tipo={acao}
        onHide={() => setAcao(null)}
        onConfirm={confirmar}
      />
      <ModalFinalizarVenda
        show={showFinalizarVenda}
        onHide={cancelarFaturamento}
        nota={nota}
        setNota={setNota}
        onConfirm={confirmarFaturamento}
      />
    </div>
  );
};

export default OrdemServicoPage;
